import Banner from '@/components/Banner';
import Chapters, { Chapter } from '@/components/Chapters';
import ImageGallery from '@/components/ImageGallery';
import { Reveal } from '@/components/Reveal';
import ShareDialog from '@/components/ShareDialog';
import Timeline from '@/components/Timeline';
import Typography from '@/components/ui/typography';
import Band from '@/Layouts/Band';
import SectionLayout from '@/Layouts/SectionLayout';
import { groupInOrder } from '@/lib/utils';

const rotaryJourney: { year: string; title: string; organization: string }[] = [
  {
    year: '2024-25',
    title: 'Assistant Governor',
    organization: 'Rotary District 3000',
  },
  {
    year: '2023-24',
    title: 'District Chairman - New Generation Service Exchange Chairman',
    organization: 'Rotary District 3000',
  },
  {
    year: '2023-24',
    title: 'Club Advisor',
    organization:
      'Rotary Club of Tiruchi Thamizhan and Rotaract Club of Tiruchi Thulirgal',
  },
  {
    year: '2022-23',
    title: 'RLI Faculty Certified Rotary Member',
    organization: 'Goa',
  },
  {
    year: '2022-23',
    title: 'District Chairman - RLI',
    organization: 'Rotary District 3000',
  },
  {
    year: '2021-22',
    title: 'Goodwill Ambassador',
    organization: 'Rotary District 3212',
  },
  {
    year: '2021-22',
    title: 'District Joint Secretary - Women Empowerment',
    organization: 'Rotary District 3000',
  },
  {
    year: '2019-20',
    title: 'District Secretary - Royal Queens',
    organization: 'Rotary District 3000',
  },
  {
    year: '2018-20',
    title: 'Charter President',
    organization: 'Rotary Club of Tiruchi Thamizhachi',
  },
  {
    year: '2016-17',
    title: 'District Chairman - Mini India',
    organization: 'Rotary District 3000',
  },
  {
    year: '2016-17',
    title: 'Hosted Mega Event',
    organization: 'Innerwheel Club of Tiruchirapalli DJ nte with Butterflies',
  },
  {
    year: '2016-17',
    title: 'Charter President',
    organization: 'Rotary Club of Tiruchirapalli Butterflies',
  },
];

const timeline = Array.from(
  groupInOrder(rotaryJourney, (role) => role.year).entries()
).map(([year, roles]) => ({
  marker: year,
  entries: roles.map((role) => ({
    title: role.title,
    detail: role.organization,
  })),
}));

/* Descriptions are the original copy; taglines and titles are new. */
const chapters: Chapter[] = [
  {
    tagline: '2016-17',
    title: 'Charter President, Butterflies',
    description:
      "In the realm of Rotary leadership, she soared as the Charter President of the Rotary Club of Tiruchirapalli Butterflies, where her stewardship earned the club the titles of Best President 2016-17 and Best Emerging Club 2016-17. Her presidency etched the club's name into the Guinness Book of Records, the Asia Book of Records, the India Book of Records and the Jetlee Book of Records.",
  },
  {
    tagline: 'Friendship exchange',
    title: 'Representing Rotary in Japan',
    description:
      'Represented Rotary as one of the six women Rotarians on a Friendship Exchange Team to Districts 2730 and 2720 in Japan, contributing to Vocational Training Teams on Winds and Literacy in District 2770.',
  },
  {
    tagline: '2019 onward',
    title: 'Chartering Tiruchi Thamizhachi',
    description:
      'In 2019, she added another feather to her cap by chartering the Rotary Club of Tiruchi Thamizhachi, marking a new chapter of accolades and impactful service to society. Her role as a trailblazer shone brightly as she introduced over 65 women Rotarians to the Rotary world, leaving an indelible mark of progress.',
  },
];

const RotaryJourney = ({ images }: { images: string[] }) => {
  const urls = images.map((image) => `/storage/${image}`);

  const chapterImages = urls.slice(1, chapters.length + 1);
  const galleryImages = urls.slice(chapters.length + 1);

  return (
    <>
      <Banner
        image={urls[0]}
        alt="Rtn Thamizhachi Priya Govindaraj at a Rotary event"
        tagline="Rotary leadership"
        title="Journey as a Rotary Member"
        description="Rtn Thamizhachi Priya Govindaraj stands as a beacon of inspiration within the Rotary community. Her tenure as a Rotarian is not merely a chapter but an illustrious saga."
      >
        <ShareDialog
          url={window.location.href}
          title="Journey as a Rotary Member"
          content="Rtn Thamizhachi Priya Govindaraj stands as a beacon of inspiration within the Rotary community. Her tenure as a Rotarian is not merely a chapter but an illustrious saga that has touched remarkable heights."
        />
      </Banner>

      <Band tone="raised" pattern="lattice">
        <SectionLayout space="lg">
          <Chapters chapters={chapters} images={chapterImages} />
        </SectionLayout>
      </Band>

      {/* The roles were a four-column grid that repeated each year above every
          entry — 2016-17 printed three times — so the sequence read as
          unordered. Grouped onto the spine, it reads as a tenure. */}
      <Band tone="surface" pattern="stitch" seam>
        <SectionLayout space="lg">
          <Reveal className="flex max-w-2xl flex-col gap-3">
            <Typography.Eyebrow>Offices held</Typography.Eyebrow>
            <Typography.Display2 className="text-2xl md:text-3xl lg:text-4xl">
              A tenure, year by year
            </Typography.Display2>
          </Reveal>

          <Timeline groups={timeline} unit={['office', 'offices']} />
        </SectionLayout>
      </Band>

      <Band tone="raised">
        <SectionLayout space="lg">
          {galleryImages.length > 0 && (
            <>
              <Reveal className="flex flex-col gap-3">
                <Typography.Eyebrow>From the district</Typography.Eyebrow>
                <Typography.Display2 className="text-2xl md:text-3xl lg:text-4xl">
                  Rotary, in pictures
                </Typography.Display2>
              </Reveal>

              <ImageGallery
                images={galleryImages}
                alt="Photographs from Rotary events and installations"
              />
            </>
          )}

          <Reveal>
            <Typography.Body className="max-w-prose text-muted-foreground">
              Her journey is a living testament to unwavering commitment,
              tireless service, and an unyielding passion for making a
              significant difference in the Rotary world — an ode to the
              transformative power of service above self.
            </Typography.Body>
          </Reveal>
        </SectionLayout>
      </Band>
    </>
  );
};

export default RotaryJourney;
